import React, { useRef, useState } from 'react';
import { Button, DragAndDrop, IconSpinner, Modal } from '../../../../components';
import { manageDisableScrolling } from '../../../../ducks/ui.duck';
import { useDispatch } from 'react-redux';
import { Field, Form as FinalForm } from 'react-final-form';
import { handleFileUpload } from '../../../../util/circleHelper';
import { FormattedMessage } from '../../../../util/reactIntl';
import css from './CreatePostForm.module.css';
import IconCard from '../../../../components/SavedCardDetails/IconCard/IconCard';

// Maximum file size: 10MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const isCoverUploadDebugEnabled = () => {
  try {
    return typeof window !== 'undefined' && Boolean(window.__COVER_IMAGE_UPLOAD_DEBUG__);
  } catch (e) {
    return false;
  }
};

const coverUploadDebugLog = (...args) => {
  if (!isCoverUploadDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log('[CoverImageUploadModal]', ...args);
};

const validateImageFile = (file) => {
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }
  
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'Please select an image file' };
  }
  
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: 'File size must be less than 10MB' };
  }
  
  return { valid: true };
};

const dragAndDropHandler = async (
  file,
  form,
  values,
  dispatch,
  onImageClick,
  formOldValues,
  setFormOldValues,
  setUploadError,
  setIsUploading
) => {
  // Validate file
  const validation = validateImageFile(file);
  if (!validation.valid) {
    setUploadError(validation.error);
    return;
  }

  setIsUploading(true);
  setUploadError(null);

  try {
    const imageUrl = await handleFileUpload(file, dispatch);
    
    if (!imageUrl || !imageUrl.signed_id) {
      throw new Error('Upload failed - no response from server');
    }

    if (formOldValues) {
      setFormOldValues({
        ...formOldValues,
        coverImage: imageUrl,
        coverImagefiles: { file: file, signed_id: imageUrl?.signed_id },
      });
    } else {
      form.change(`coverImage`, imageUrl);
      form.change(`coverImagefiles`, { file: file, signed_id: imageUrl?.signed_id });
    }
    
    onImageClick();
  } catch (error) {
    console.error('Upload error:', error);
    setUploadError(error.message || 'Failed to upload image. Please try again.');
  } finally {
    setIsUploading(false);
  }
};

const ImageFieldComponent = props => {
  const { form, values, inputRef, onImageClick, formOldValues, setFormOldValues, setUploadError, setIsUploading } = props;
  const dispatch = useDispatch();
  
  return (
    <Field
      name="coverImage"
      component={() => {
        const onChange = async e => {
          const file = e.target?.files?.[0];
          coverUploadDebugLog('input:onChange', {
            hasFile: Boolean(file),
            name: file?.name,
            type: file?.type,
            size: file?.size,
          });
          if (!file) return;

          // Validate file
          const validation = validateImageFile(file);
          if (!validation.valid) {
            setUploadError(validation.error);
            return;
          }

          setIsUploading(true);
          setUploadError(null);

          coverUploadDebugLog('upload:start', { name: file?.name, size: file?.size, type: file?.type });

          try {
            const imageUrl = await handleFileUpload(file, dispatch);
            coverUploadDebugLog('upload:response', imageUrl);
            
            if (!imageUrl || !imageUrl.signed_id) {
              throw new Error('Upload failed - no response from server');
            }

            if (formOldValues) {
              setFormOldValues({
                ...formOldValues,
                coverImage: imageUrl,
                coverImagefiles: { file: file, signed_id: imageUrl?.signed_id },
              });
            } else {
              form.change(`coverImage`, imageUrl);
              form.change(`coverImagefiles`, { file: file, signed_id: imageUrl?.signed_id });
            }
            
            onImageClick();
          } catch (error) {
            console.error('Upload error:', error);
            coverUploadDebugLog('upload:error', {
              message: error?.message,
              name: error?.name,
              stack: error?.stack,
            });
            setUploadError(error.message || 'Failed to upload image. Please try again.');
          } finally {
            setIsUploading(false);
            coverUploadDebugLog('upload:finish');
          }
        };
        
        const inputProps = {
          accept: 'image/*',
          onChange,
          type: 'file',
          // Reset value to allow selecting the same file again
          onClick: (e) => {
            coverUploadDebugLog('input:onClick (reset value)');
            e.target.value = null;
          }
        };
        
        return (
          <div className={css.visuallyHiddenFileInput}>
            <input ref={inputRef} {...inputProps} />
          </div>
        );
      }}
    />
  );
};

const CoverImageUploadModal = props => {
  const {
    isImageModal,
    onImageClick,
    form,
    values,
    requestDirectUpload,
    formOldValues,
    setFormOldValues,
  } = props;
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleOpenFileInput = (e) => {
    e.preventDefault();
    e.stopPropagation();

    coverUploadDebugLog('chooseImage:click', {
      hasInputRef: Boolean(inputRef.current),
      isUploading,
      requestDirectUpload,
      isImageModal,
    });
    
    // Prevent opening file dialog if already uploading
    if (isUploading || requestDirectUpload) {
      coverUploadDebugLog('chooseImage:blocked');
      return;
    }
    
    // Clear previous errors
    setUploadError(null);
    
    if (inputRef.current) {
      coverUploadDebugLog('input:click()');
      inputRef.current.click();
    } else {
      coverUploadDebugLog('inputRef missing on click attempt');
    }
  };

  // Detect if device is mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  
  // Show loading state
  const showLoader = requestDirectUpload || isUploading;

  return (
    <div>
      <Modal
        id={'coverImageUploadModal'}
        isOpen={isImageModal}
        onClose={onImageClick}
        onManageDisableScrolling={manageDisableScrolling}
        className={css.uploadModals}
      >
        <div className={css.contentWrapper}>
          {showLoader ? (
            <div className={css.loader}>
              <IconSpinner type={true} />
              <p style={{ marginTop: '10px', textAlign: 'center' }}>Uploading image...</p>
            </div>
          ) : (
            <>
              {/* Display error message if upload fails */}
              {uploadError && (
                <div className={css.coverImageUploadError}>
                  {uploadError}
                </div>
              )}
              
              {/* On mobile, show simplified UI without drag and drop */}
              {isMobile ? (
                <div className={css.uploadWrapper}>
                  <div className={css.headingName}>
                    <FormattedMessage id="CoverImageUploadModal.attachImage" />
                  </div>
                  <div className={css.boxUploader}>
                    <div className={css.plusIcon}>
                      <span className={css.icon}>
                        <IconCard brand="plus" />
                      </span>
                    </div>
                    <div className={css.dragText}>
                      <FormattedMessage id="CoverImageUploadModal.tapToSelect" />
                    </div>
                    <Button
                      onClick={handleOpenFileInput}
                      type="button"
                      className={css.chooseFileButton}
                      disabled={isUploading}
                    >
                      <FormattedMessage id="CoverImageUploadModal.chooseImage" />
                    </Button>
                    <ImageFieldComponent
                      form={form}
                      values={values}
                      inputRef={inputRef}
                      onImageClick={onImageClick}
                      formOldValues={formOldValues}
                      setFormOldValues={setFormOldValues}
                      setUploadError={setUploadError}
                      setIsUploading={setIsUploading}
                    />
                  </div>
                </div>
              ) : (
                /* On desktop, show drag and drop UI */
                <DragAndDrop
                  handleDrop={files =>
                    dragAndDropHandler(
                      files[0],
                      form,
                      values,
                      dispatch,
                      onImageClick,
                      formOldValues,
                      setFormOldValues,
                      setUploadError,
                      setIsUploading
                    )
                  }
                  acceptedFileType={'image'}
                >
                  <div className={css.uploadWrapper}>
                    <div className={css.headingName}>
                      <FormattedMessage id="CoverImageUploadModal.attachImage" />
                    </div>
                    <div className={css.boxUploader}>
                      <div className={css.plusIcon}>
                        <span className={css.icon}>
                          <IconCard brand="plus" />
                        </span>
                      </div>
                      <div className={css.dragText}>
                        <FormattedMessage id="CoverImageUploadModal.dropImageHere" />
                      </div>
                      <Button
                        onClick={handleOpenFileInput}
                        type="button"
                        className={css.chooseFileButton}
                        disabled={isUploading}
                      >
                        <FormattedMessage id="CoverImageUploadModal.chooseImage" />
                      </Button>

                      <ImageFieldComponent
                        form={form}
                        values={values}
                        inputRef={inputRef}
                        onImageClick={onImageClick}
                        formOldValues={formOldValues}
                        setFormOldValues={setFormOldValues}
                        setUploadError={setUploadError}
                        setIsUploading={setIsUploading}
                      />
                    </div>
                  </div>
                </DragAndDrop>
              )}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CoverImageUploadModal;
